import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Shield, 
  CheckCircle, 
  ArrowRight, 
  Layers, 
  FileCheck, 
  AlertTriangle,
  BarChart3,
  Zap,
  Lock,
  Globe,
  Users,
  Clock,
  Bug,
  Scan,
  Activity,
  Target,
  ShieldAlert,
  Radio,
  Radar,
  Search,
  Eye,
  Crosshair
} from 'lucide-react';

const features = [
  {
    icon: Layers,
    title: 'Multi-Framework Compliance',
    description: 'SOC 2, ISO 27001, GDPR, HIPAA, PCI-DSS, NIST, FedRAMP - all in one platform.'
  },
  {
    icon: Zap,
    title: 'Map Once, Comply Many',
    description: 'Our Common Control Framework maps one security control to multiple regulations simultaneously.'
  },
  {
    icon: FileCheck,
    title: 'Automated Evidence Collection',
    description: 'Connect AWS, GitHub, Okta and automatically pull compliance evidence.'
  },
  {
    icon: AlertTriangle,
    title: 'AI-Powered Risk Assessment',
    description: 'Gemini AI analyzes risks and suggests impact scores with mitigation recommendations.'
  },
  {
    icon: BarChart3,
    title: 'Real-Time Compliance Monitoring',
    description: 'Track compliance drift with instant Slack and Teams alerts when scores drop.'
  },
  {
    icon: Lock,
    title: 'Audit-Ready Reports',
    description: 'Generate PDF compliance reports and provide read-only auditor portal access.'
  },
  {
    icon: Radar,
    title: 'CTEM Module',
    description: 'Continuous Threat Exposure Management with 5-phase lifecycle: Scoping, Discovery, Prioritization, Validation & Mobilization.'
  }
];

// NEW: Vulnerability Management Features
const vulnFeatures = [
  {
    icon: Scan,
    title: 'Vulnerability Scanner',
    description: 'Automated scanning with quick and deep assessment modes. CVSS scoring and CVE/CWE tracking.'
  },
  {
    icon: Activity,
    title: 'Continuous Monitoring',
    description: 'Real-time threat feeds for zero-day exploits, patch availability, and emerging threats.'
  },
  {
    icon: Clock,
    title: 'SLA-Based Remediation',
    description: 'Automatic SLA deadlines based on severity with escalation workflows to responsible teams.'
  },
  {
    icon: Target,
    title: 'Attack Type Coverage',
    description: 'Phishing, Ransomware, DDoS, MITM, SQL Injection, XSS, Zero-Day, DNS Spoofing & more.'
  }
];

const attackTypes = [
  { name: 'Phishing', icon: '🎣' },
  { name: 'Ransomware', icon: '🔒' },
  { name: 'DDoS', icon: '🌊' },
  { name: 'MITM', icon: '👤' },
  { name: 'SQL Injection', icon: '💉' },
  { name: 'XSS', icon: '📜' },
  { name: 'Zero-Day', icon: '⚡' },
  { name: 'DNS Spoofing', icon: '🌐' }
];

const frameworks = [
  { name: 'SOC 2', controls: 49, color: 'bg-blue-100 text-blue-700' },
  { name: 'ISO 27001', controls: 54, color: 'bg-green-100 text-green-700' },
  { name: 'GDPR', controls: 36, color: 'bg-purple-100 text-purple-700' },
  { name: 'HIPAA', controls: 30, color: 'bg-red-100 text-red-700' },
  { name: 'PCI-DSS', controls: 78, color: 'bg-orange-100 text-orange-700' },
  { name: 'NIST 800-53', controls: 110, color: 'bg-slate-100 text-slate-700' },
  { name: 'FedRAMP', controls: 85, color: 'bg-indigo-100 text-indigo-700' }
];

const stats = [
  { value: '442+', label: 'Pre-built Controls' },
  { value: '7', label: 'Compliance Frameworks' },
  { value: '11+', label: 'Attack Types Covered' },
  { value: '5-Phase', label: 'CTEM Lifecycle' }
];

const testimonials = [
  {
    quote: "Zentry cut our SOC 2 preparation time in half. The control mapping feature alone saved us weeks of duplicate work.",
    author: "Sarah Chen",
    role: "CISO, TechStart Inc.",
    avatar: "SC"
  },
  {
    quote: "Finally, a GRC platform that doesn't require a six-figure budget. Perfect for growing companies.",
    author: "Michael Roberts",
    role: "VP of Engineering, DataFlow",
    avatar: "MR"
  },
  {
    quote: "The AI risk assessment is a game-changer. What used to take days now takes minutes.",
    author: "Emily Watson",
    role: "Compliance Manager, HealthTech Co.",
    avatar: "EW"
  }
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-slate-900 rounded flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900">Zentry</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#vulnerability" className="text-slate-600 hover:text-slate-900 transition">Vulnerability</a>
            <a href="#ctem" className="text-slate-600 hover:text-slate-900 transition">CTEM</a>
            <a href="#features" className="text-slate-600 hover:text-slate-900 transition">Features</a>
            <a href="#frameworks" className="text-slate-600 hover:text-slate-900 transition">Frameworks</a>
            <Link to="/pricing" className="text-slate-600 hover:text-slate-900 transition">Pricing</Link>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-slate-600">Sign In</Button>
            </Link>
            <Link to="/register">
              <Button className="bg-slate-900 hover:bg-slate-800">Start Free Trial</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-full text-sm text-slate-600 mb-8">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              Trusted by 500+ companies worldwide
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-slate-900 leading-tight mb-6">
              Compliance Management,{' '}
              <span className="text-blue-600">Simplified</span>
            </h1>
            <p className="text-xl text-slate-600 mb-10 leading-relaxed">
              Zentry is the enterprise GRC platform that helps you achieve SOC 2, ISO 27001, 
              HIPAA, and more—all from a single dashboard. Map once, comply with many frameworks.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/register">
                <Button size="lg" className="bg-slate-900 hover:bg-slate-800 text-lg px-8 py-6">
                  Start 14-Day Free Trial
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button size="lg" variant="outline" className="text-lg px-8 py-6">
                  View Pricing
                </Button>
              </Link>
            </div>
            <p className="text-sm text-slate-500 mt-4">No credit card required • Cancel anytime</p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-slate-900 mb-2">{stat.value}</div>
                <div className="text-slate-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vulnerability Management Section - NEW */}
      <section id="vulnerability" className="py-24 px-6 bg-gradient-to-br from-red-50 to-orange-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 rounded-full text-sm text-red-700 mb-6">
              <ShieldAlert className="w-4 h-4" />
              NEW: Enterprise Vulnerability Management
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Complete Vulnerability Management Suite
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Industry-grade vulnerability scanning, continuous monitoring, and SLA-based remediation workflows.
              Protect your organization from modern cyber threats.
            </p>
          </div>
          
          {/* Vuln Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {vulnFeatures.map((feature, index) => (
              <div key={index} className="p-6 bg-white border border-red-100 rounded-lg hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
          
          {/* Attack Types */}
          <div className="bg-white rounded-xl p-8 border border-red-100">
            <h3 className="text-xl font-semibold text-slate-900 mb-6 text-center">Attack Types Covered</h3>
            <div className="flex flex-wrap justify-center gap-4">
              {attackTypes.map((attack, index) => (
                <div key={index} className="flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-full border border-slate-200">
                  <span className="text-lg">{attack.icon}</span>
                  <span className="font-medium text-slate-700">{attack.name}</span>
                </div>
              ))}
            </div>
            <div className="mt-8 text-center">
              <p className="text-slate-500 mb-4">
                SLA-based remediation: Critical (24h) • High (7 days) • Medium (30 days) • Low (90 days)
              </p>
              <Link to="/register">
                <Button className="bg-red-600 hover:bg-red-700">
                  <Bug className="w-4 h-4 mr-2" />
                  Start Vulnerability Assessment
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTEM Section - Continuous Threat Exposure Management */}
      <section id="ctem" className="py-24 px-6 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-sm text-emerald-400 mb-6">
                <Radar className="w-4 h-4" />
                NEW: Continuous Threat Exposure Management
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Proactive Threat Management with CTEM
              </h2>
              <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                Go beyond reactive security. Zentry&apos;s CTEM module continuously discovers, prioritizes, 
                and validates threat exposures across your attack surface — before attackers exploit them.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-emerald-500/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Search className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">Auto-Discovery</h4>
                    <p className="text-sm text-slate-400">Automatically discover exposures via NVD feeds, MITRE ATT&amp;CK, and integrated vulnerability scanners.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-emerald-500/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Crosshair className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">Risk-Based Prioritization</h4>
                    <p className="text-sm text-slate-400">Prioritize threats by business impact, exploitability, and asset criticality — not just CVSS scores.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-emerald-500/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Eye className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">Validation & Mobilization</h4>
                    <p className="text-sm text-slate-400">Validate findings, assign remediation owners, and track mobilization across your security team.</p>
                  </div>
                </div>
              </div>
              <Link to="/register">
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  <Radar className="w-4 h-4 mr-2" />
                  Explore CTEM Module
                </Button>
              </Link>
            </div>
            <div className="space-y-4">
              <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">5-Phase CTEM Lifecycle</h3>
                <div className="space-y-3">
                  {[
                    { phase: '1. Scoping', desc: 'Define your attack surface and critical assets', color: 'bg-blue-500' },
                    { phase: '2. Discovery', desc: 'Automated exposure detection from multiple feeds', color: 'bg-amber-500' },
                    { phase: '3. Prioritization', desc: 'AI-driven severity ranking by business context', color: 'bg-orange-500' },
                    { phase: '4. Validation', desc: 'Verify exploitability and eliminate false positives', color: 'bg-red-500' },
                    { phase: '5. Mobilization', desc: 'Assign, track, and close remediation workflows', color: 'bg-emerald-500' },
                  ].map((step, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${step.color} flex-shrink-0`}></div>
                      <div className="flex-1">
                        <span className="text-white font-medium text-sm">{step.phase}</span>
                        <span className="text-slate-400 text-sm ml-2">— {step.desc}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-emerald-400">24/7</div>
                  <div className="text-xs text-slate-400 mt-1">Continuous Monitoring</div>
                </div>
                <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-emerald-400">NVD</div>
                  <div className="text-xs text-slate-400 mt-1">Real-Time Feeds</div>
                </div>
                <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-emerald-400">MITRE</div>
                  <div className="text-xs text-slate-400 mt-1">ATT&amp;CK Mapped</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Everything You Need for Compliance
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              From evidence collection to audit reports, Zentry handles the entire compliance lifecycle.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="p-6 bg-white border border-slate-200 rounded-lg hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-slate-700" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Frameworks Section */}
      <section id="frameworks" className="py-24 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              7 Frameworks, One Platform
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Pre-loaded with industry-standard compliance frameworks and ready to use out of the box.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {frameworks.map((framework, index) => (
              <div key={index} className={`px-6 py-4 rounded-lg ${framework.color} flex items-center gap-3`}>
                <CheckCircle className="w-5 h-5" />
                <span className="font-semibold">{framework.name}</span>
                <span className="text-sm opacity-75">({framework.controls} controls)</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Get Compliant in 3 Steps
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-blue-600">1</span>
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Select Frameworks</h3>
              <p className="text-slate-600">Choose the compliance frameworks relevant to your business.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-blue-600">2</span>
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Connect & Collect</h3>
              <p className="text-slate-600">Integrate your tools and automatically gather compliance evidence.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-blue-600">3</span>
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Monitor & Report</h3>
              <p className="text-slate-600">Track compliance in real-time and generate audit-ready reports.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 px-6 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Trusted by Security Teams
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-slate-800 p-6 rounded-lg">
                <p className="text-slate-300 mb-6 italic">&quot;{testimonial.quote}&quot;</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <div className="text-white font-medium">{testimonial.author}</div>
                    <div className="text-slate-400 text-sm">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Ready to Simplify Compliance?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Join hundreds of companies using Zentry to streamline their GRC processes.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="bg-slate-900 hover:bg-slate-800 text-lg px-8 py-6">
                Start Your Free Trial
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button size="lg" variant="outline" className="text-lg px-8 py-6">
                Compare Plans
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-slate-900 rounded flex items-center justify-center">
                  <Shield className="w-4 h-4 text-white" />
                </div>
                <span className="text-lg font-bold text-slate-900">Zentry</span>
              </div>
              <p className="text-slate-600 text-sm">
                Enterprise GRC platform for modern compliance teams.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-slate-900 mb-4">Product</h4>
              <ul className="space-y-2 text-sm text-slate-600">
                <li><a href="#features" className="hover:text-slate-900">Features</a></li>
                <li><Link to="/pricing" className="hover:text-slate-900">Pricing</Link></li>
                <li><a href="#frameworks" className="hover:text-slate-900">Frameworks</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-slate-900 mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-slate-600">
                <li><a href="#" className="hover:text-slate-900">About</a></li>
                <li><a href="#" className="hover:text-slate-900">Blog</a></li>
                <li><a href="#" className="hover:text-slate-900">Careers</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-slate-900 mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-slate-600">
                <li><Link to="/privacy" className="hover:text-slate-900">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-slate-900">Terms of Service</Link></li>
                <li><a href="#" className="hover:text-slate-900">Security</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-200 mt-12 pt-8 text-center text-sm text-slate-600">
            © 2026 Zentry. All rights reserved. | <Link to="/terms" className="hover:text-slate-900">Terms</Link> | <Link to="/privacy" className="hover:text-slate-900">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
